import { useCallback, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	isUpdatableAction,
	isEphemeralCreateAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import { IMAGE_CREATE_KEY } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type {
	StreamActionClassification,
	StreamActionComplete,
	StreamActionError,
	StreamActionPart,
	StreamActionPrepare,
	StreamAnswerPart,
	StreamError,
	StreamResponse,
	StreamTrace,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useChatContextEditorContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ROVO_INLINE_INPUT_BOX_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-inline-input-box-client';
import { InlineInputBoxErrorMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-error-message-command';
import { InlineInputBoxFinalResponseMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-final-response-message-command';
import { InlineInputBoxTraceMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-trace-message-command';

import { useUIConfig } from '../../../../common/utils/ui-config/useUIConfig';
import { useBridgeSendCommand } from '../../context/useBridgeSendCommand';
import { clearAIGeneratingState } from '../../utils/clear-ai-generating-state';

export type PromptStreamHook = {
	onTraceMessage?: (
		message: StreamTrace,
		invocationId: string,
		openChat?: boolean,
	) => Promise<void>;
	onActionClassification?: (
		actionClassification: StreamActionClassification,
		messageId: string,
		conversationId: string,
	) => Promise<void>;
	onActionPart?: (
		action: StreamActionPart | StreamActionPrepare,
		messageId: string,
		conversationId: string,
		openChat?: boolean,
	) => Promise<void>;
	onActionComplete?: (action: StreamActionComplete, messageId: string) => Promise<void>;
	onAnswerPart?: (
		answer: StreamAnswerPart,
		messageId: string,
		conversationId: string,
	) => Promise<void>;
	onFinalResponse?: (response: StreamResponse, invocationId: string) => Promise<void>;
	onError?: (
		error: StreamError | StreamActionError,
		invocationId: string,
		options?: { openChatOnError?: boolean; conversationId?: string },
	) => Promise<void>;
	onSafetyViolation?: (conversationId: string) => void;
};

export const usePromptStream = (): PromptStreamHook => {
	const { sendCommand } = useBridgeSendCommand();
	const [{ isViewMode: editorIsViewMode } = {}] = useChatContextEditorContext();
	const publish = usePublish('ai-mate');
	const { mounted } = useUIStore();
	const { elements } = useUIConfig();

	// Track which messages have already called publish to avoid duplicate calls
	const publishedMessagesRef = useRef<Set<string>>(new Set());

	// Track which messages have update actions to prevent processing publishChatOpen
	const messagesWithUpdateActionsRef = useRef<Set<string>>(new Set());

	const stagingAreaEnabled = useMemo(
		() => elements?.stagingAreaEnabled,
		[elements?.stagingAreaEnabled],
	);

	const publishChatOpen = useCallback(
		(conversationId: string, messageId: string, openChat?: boolean): void => {
			if (!publishedMessagesRef.current.has(messageId) && !mounted && !stagingAreaEnabled) {
				publishedMessagesRef.current.add(messageId);
				publish({
					type: 'chat-open',
					source: 'input-prompt-box',
					openChat,
					data: {
						channelId: conversationId,
					},
				});
			}
		},
		[publish, stagingAreaEnabled, mounted],
	);

	const publishEphemeralPreviewOpen = useCallback(
		(conversationId: string, messageId: string): void => {
			if (!publishedMessagesRef.current.has(messageId) && !mounted && !stagingAreaEnabled) {
				publishedMessagesRef.current.add(messageId);
				publish({
					type: 'open-smart-creation-modal',
					source: 'input-prompt-box',
					openChat: false,
					data: {
						channelId: conversationId,
						entryPoint: 'dynamicUiFromInlineEditorToolbarAskRovo',
						stagingAreaActive: true,
						isFloating: true,
						shouldShowBackButton: false,
					},
				});
			}
		},
		[publish, stagingAreaEnabled, mounted],
	);

	const onTraceMessage = useCallback(
		({ message }: StreamTrace, invocationId: string): Promise<void> => {
			const command = sendCommand(InlineInputBoxTraceMessageCommand, {
				message,
				receiverId: ROVO_INLINE_INPUT_BOX_CLIENT_ID,
				groupId: invocationId,
				buffer: false,
			});

			return command.result();
		},
		[sendCommand],
	);

	const onActionComplete = useCallback(
		(action: StreamActionComplete, messageId: string): Promise<void> => {
			return new Promise<void>((resolve) => {
				// Backend-persisted edits stream no incremental action parts, so completion is the
				// only signal that the document was updated before the answer arrives.
				// Ephemeral create actions are left to the action part flow, which opens a preview.
				if (
					!isEphemeralCreateAction(action.message) &&
					isUpdatableAction(action.message) &&
					fg('platform_editor_ai_fix_update_backend_no_chat_open')
				) {
					messagesWithUpdateActionsRef.current.add(messageId);
				}

				resolve();
			});
		},
		[],
	);

	const onActionPart = useCallback(
		(
			actionPart: StreamActionPart | StreamActionPrepare,
			messageId: string,
			conversationId: string,
			openChat?: boolean,
		): Promise<void> => {
			return new Promise<void>((resolve) => {
				// Ephemeral create actions must be checked before update actions, since they are update actions too.
				if (isEphemeralCreateAction(actionPart.message)) {
					if (!editorIsViewMode && fg('cc-maui-phase-2')) {
						// When phase-2 is enabled and in edit mode, suppress ephemeral preview —
						// the inline editor handles it. In view mode, always open the preview.
						messagesWithUpdateActionsRef.current.add(messageId);
					} else {
						// When phase-2 is disabled, open the AIFC modal in ephemeral preview mode
						publishEphemeralPreviewOpen(conversationId, messageId);
					}
				} else if (isUpdatableAction(actionPart.message)) {
					// Temporary Patch to not open Rovo Chat on update actions
					messagesWithUpdateActionsRef.current.add(messageId);
				} else {
					publishChatOpen(conversationId, messageId, openChat);
				}

				resolve();
			});
		},
		[publishChatOpen, publishEphemeralPreviewOpen, editorIsViewMode],
	);

	const onAnswerPart = useCallback(
		(_: StreamAnswerPart, messageId: string, conversationId: string): Promise<void> => {
			return new Promise<void>((resolve) => {
				if (!messagesWithUpdateActionsRef.current.has(messageId)) {
					publishChatOpen(conversationId, messageId);
				}
				resolve();
			});
		},
		[publishChatOpen],
	);

	const onFinalResponse = useCallback(
		(response: StreamResponse, invocationId: string): Promise<void> => {
			const command = sendCommand(InlineInputBoxFinalResponseMessageCommand, {
				receiverId: ROVO_INLINE_INPUT_BOX_CLIENT_ID,
				groupId: invocationId,
				buffer: false,
			});

			// Clean up message tracking for this invocation
			messagesWithUpdateActionsRef.current.delete(invocationId);

			return command.result();
		},
		[sendCommand],
	);

	const onError = useCallback(
		(
			error: StreamError | StreamActionError,
			invocationId: string,
			options?: { openChatOnError?: boolean; conversationId?: string },
		): Promise<void> => {
			// If the error is for an image-create action, signal the editor bridge to
			// clear the AI generating state from the existing image node
			if (
				'message' in error &&
				error.message &&
				typeof error.message === 'object' &&
				'key' in error.message &&
				error.message.key === IMAGE_CREATE_KEY
			) {
				const errorData =
					'data' in error.message
						? (error.message.data as Record<string, unknown> | undefined)
						: undefined;
				clearAIGeneratingState(sendCommand, {
					contentId: errorData?.contentId as string | undefined,
					groupId: invocationId,
				});
			}

			const command = sendCommand(InlineInputBoxErrorMessageCommand, {
				error: {
					message: error.message,
					metadata: error.metadata,
				},
				receiverId: ROVO_INLINE_INPUT_BOX_CLIENT_ID,
				groupId: invocationId,
				buffer: false,
				openChatOnError: options?.openChatOnError,
				conversationId: options?.conversationId,
			});

			// Clean up message tracking for this invocation
			messagesWithUpdateActionsRef.current.delete(invocationId);

			return command.result();
		},
		[sendCommand],
	);

	return useMemo(
		() => ({
			onTraceMessage,
			onActionPart,
			onActionComplete,
			onAnswerPart,
			onFinalResponse,
			onError,
		}),
		[onTraceMessage, onActionPart, onActionComplete, onAnswerPart, onFinalResponse, onError],
	);
};
